import { useEffect, useState } from "react";
import { useIdleTimer } from "react-idle-timer";

type IdleTimer = ReturnType<typeof useIdleTimer>;

export interface IdleSessionOptions {
  timeoutMs?: number;
  warningMs?: number;
  disabled?: boolean;
  onPrompt?: (event?: Event, idleTimer?: IdleTimer) => void;
  onIdle?: (event?: Event, idleTimer?: IdleTimer) => void;
  onActive?: (event?: Event, idleTimer?: IdleTimer) => void;
  onAction?: (event?: Event, idleTimer?: IdleTimer) => void;
}

export interface IdleSessionState {
  idleTimer: IdleTimer;
  isPrompted: boolean;
  warningSeconds: number;
  resetTimer: () => void;
  closePrompt: () => void;
}

const DEFAULT_TIMEOUT_MS = 10 * 60 * 1000;
const DEFAULT_WARNING_MS = 60 * 1000;

export const useIdleSession = ({
  timeoutMs = DEFAULT_TIMEOUT_MS,
  warningMs = DEFAULT_WARNING_MS,
  disabled = false,
  onPrompt,
  onIdle,
  onActive,
  onAction,
}: IdleSessionOptions = {}): IdleSessionState => {
  const [isPrompted, setIsPrompted] = useState(false);
  const [warningSeconds, setWarningSeconds] = useState(Math.ceil(warningMs / 1000));

  const idleTimer = useIdleTimer({
    timeout: timeoutMs,
    promptBeforeIdle: warningMs,
    crossTab: true,

    onPrompt: (event, timer) => {
      setIsPrompted(true);
      setWarningSeconds(Math.max(0, Math.ceil((timer?.getRemainingTime() ?? warningMs) / 1000)));
      onPrompt?.(event, timer);
    },

    onIdle: (event, timer) => {
      setIsPrompted(false);
      onIdle?.(event, timer);
    },

    onActive: (event, timer) => {
      // ✅ reset only when coming back AFTER idle
      setIsPrompted(false);
      onActive?.(event, timer);
    },

    onAction: (event, timer) => {
      //DO NOT close popup on scroll/mouse move
      onAction?.(event, timer);
    },

    disabled,
  });

  useEffect(() => {
    if (!isPrompted) return;

    const interval = window.setInterval(() => {
      setWarningSeconds(Math.max(0, Math.ceil(idleTimer.getRemainingTime() / 1000)));
    }, 1000);

    return () => window.clearInterval(interval);
  }, [isPrompted, idleTimer]);

  const resetTimer = () => {
    setIsPrompted(false);
    idleTimer.reset();
  };

  const closePrompt = () => {
    setIsPrompted(false);
  };

  return {
    idleTimer,
    isPrompted,
    warningSeconds,
    resetTimer,
    closePrompt,
  };
};
